import {
  Badge,
  Box,
  Collapse,
  Grid,
  Heading,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  SummaryGroupItem,
  SummaryHeaderItem,
} from "@src/components/modules/projects/detail/Evaluation/Summary";
import { formatCurrency } from "@src/components/modules/projects/detail/Evaluation/utils";
import { LoadingOverlay } from "@src/components/ui-kit";
import CollapseAllButton from "@src/components/ui-kit/DataTable/CollapseAllButton";
import { Icon } from "@src/components/ui-kit/Icon";
import { percent } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { DETAIL_TEMPLATE_COLUMN } from ".";
import type { ProjectReportStore } from "./store";

type Props = {
  store: ProjectReportStore;
};

export const ModalProjectReportDetail = observer(
  function ProjectReportDetailModal({ store }: Props) {
    return (
      <Modal isOpen={store.isDetailModalOpen} onClose={store.closeDetailModal}>
        <ModalOverlay />
        <ModalContent minW="calc(100% - 200px)">
          <ModalCloseButton />
          <ModalBody>
            <Stack flexDir="column">
              <Grid columnGap="4" templateColumns={DETAIL_TEMPLATE_COLUMN}>
                <HStack spacing="2">
                  <CollapseAllButton onClick={store.toggleCollapseAll} />
                  <Heading fontSize="xl">
                    <Trans>Project Summary</Trans>
                  </Heading>
                </HStack>
                <SummaryHeaderItem
                  title={t`Invoiced`}
                  tooltip={t`Invoiced amount.`}
                  value={formatCurrency(store.selectedDetailSummary.billings)}
                />
                {/*
                  <SummaryHeaderItem
                    title={t`Costs`}
                    // TODO: Change tooltip
                    tooltip={t`Costs.`}
                    value={formatCurrency(0)}
                  />
                  */}
                <SummaryHeaderItem
                  title={t`Net Profit`}
                  tooltip={t`It is a summary of Profit (Work, Production, Media) + Extras EBI.`}
                  value={formatCurrency(store.selectedDetailSummary.netProfit)}
                  color={
                    store.selectedDetailSummary.netProfit > 0
                      ? "green.500"
                      : "red.500"
                  }
                />
                <SummaryHeaderItem
                  title={t`Net Margin`}
                  tooltip={t`(EBIT)`}
                  value={percent.format(store.selectedDetailSummary.netMargin)}
                  color={
                    store.selectedDetailSummary.netMargin > 0
                      ? "green.500"
                      : "red.500"
                  }
                />
                <SummaryHeaderItem
                  title={t`Gross income`}
                  tooltip={t`It is a summary of Revenue (Work) + Profit (Production, Media) + Extras EBI.`}
                  value={formatCurrency(store.selectedDetailGrossIncome)}
                />
              </Grid>
              <LoadingOverlay
                isLoading={store.fetchingProjectDetail}
                position="absolute"
                minH="48"
                size="lg"
                thickness="2px"
              >
                {store.renderableDetails.map(([category, detailRow], index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: Category has no unique identifier
                  <Box key={category + index} w="full">
                    <Grid
                      alignItems="center"
                      columnGap="4"
                      templateColumns={DETAIL_TEMPLATE_COLUMN}
                      py="2"
                    >
                      <HStack spacing="2">
                        <IconButton
                          visibility={
                            detailRow.rows.length > 0 ? "visible" : "hidden"
                          }
                          aria-label={t`Collapse handler`}
                          icon={
                            <Icon
                              name="chevron-down"
                              transform={
                                store.collapsedRows.has(category)
                                  ? undefined
                                  : "rotate(180deg)"
                              }
                            />
                          }
                          onClick={() => store.handleCollapse(category)}
                          variant="ghost"
                        />
                        <Text color="blue.600" fontSize="md" fontWeight="bold">
                          {category}
                        </Text>
                      </HStack>
                      <SummaryGroupItem
                        value={formatCurrency(detailRow.summary.billings)}
                      />
                      {/* Costs 
                      <SummaryGroupItem value={formatCurrency(0)} />
                      */}
                      <SummaryGroupItem
                        value={formatCurrency(detailRow.summary.netProfit)}
                        color={
                          detailRow.summary.netProfit < 0
                            ? "red.500"
                            : undefined
                        }
                      />
                      <SummaryGroupItem
                        value={percent.format(detailRow.summary.netMargin)}
                        color={
                          detailRow.summary.netMargin < 0
                            ? "red.500"
                            : undefined
                        }
                      />
                      <Box />
                    </Grid>
                    <Collapse
                      animateOpacity
                      in={!store.collapsedRows.has(category)}
                    >
                      {detailRow.rows.map((detail) => (
                        <Grid
                          key={detail.id + detail.title}
                          columnGap="4"
                          templateColumns={DETAIL_TEMPLATE_COLUMN}
                          py="2"
                        >
                          <VStack alignItems="start" ml="10" pl="2">
                            <Text> {detail.title}</Text>
                            {!!detail.completed_at && (
                              <Badge>
                                <Trans>Completed</Trans>
                              </Badge>
                            )}
                          </VStack>
                          <Text>
                            {formatCurrency(detail.stats.billings ?? 0)}
                          </Text>
                          {/* Costs 
                          <Text>{formatCurrency(0)}</Text>
                          */}
                          <Text
                            color={
                              (detail.stats.profit ?? 0) < 0
                                ? "red.500"
                                : undefined
                            }
                          >
                            {formatCurrency(detail.stats.profit ?? 0)}
                          </Text>
                          <Text
                            color={
                              detail.stats.profit_percent < 0
                                ? "red.500"
                                : undefined
                            }
                          >
                            {percent.format(detail.stats.profit_percent)}
                          </Text>
                          <Box />
                        </Grid>
                      ))}
                    </Collapse>
                  </Box>
                ))}
              </LoadingOverlay>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  },
);
